/**
 * Entry Filters Component
 *
 * Filter controls for entry list.
 */

import { useState } from 'react';
import type { EntryFilters, EntryType, SourceSystem } from '@/types/entry';
import { ENTRY_TYPE_ICONS, ENTRY_TYPE_LABELS, SOURCE_SYSTEM_LABELS } from '@/types/entry';

interface EntryFiltersBarProps {
  filters: EntryFilters;
  onFiltersChange: (filters: EntryFilters) => void;
}

const ENTRY_TYPES: EntryType[] = [
  'conversation',
  'meeting',
  'journal',
  'check_in',
  'voice_memo',
  'brainstorm',
  'call',
];

const SOURCE_SYSTEMS: SourceSystem[] = [
  'plaud',
  'otter',
  'manual',
  'zoom',
  'teams',
  'google_meet',
];

export function EntryFiltersBar({ filters, onFiltersChange }: EntryFiltersBarProps) {
  const [showMore, setShowMore] = useState(false);

  const handleTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      entryType: value === 'all' ? undefined : (value as EntryType),
    });
  };

  const handleSourceChange = (value: string) => {
    onFiltersChange({
      ...filters,
      sourceSystem: value === 'all' ? undefined : (value as SourceSystem),
    });
  };

  const handleSortChange = (field: 'recordedAt' | 'createdAt' | 'title') => {
    if (filters.sortBy === field) {
      // Toggle order
      onFiltersChange({
        ...filters,
        sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc',
      });
    } else {
      onFiltersChange({
        ...filters,
        sortBy: field,
        sortOrder: 'desc',
      });
    }
  };

  const handleOverridesChange = (value: string) => {
    onFiltersChange({
      ...filters,
      hasOverrides: value === 'all' ? undefined : value === 'yes',
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      sortBy: 'recordedAt',
      sortOrder: 'desc',
    });
  };

  const hasActiveFilters =
    filters.entryType ||
    filters.sourceSystem ||
    filters.hasOverrides !== undefined;

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      {/* Primary filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Entry type filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Type:</label>
          <select
            value={filters.entryType || 'all'}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Types</option>
            {ENTRY_TYPES.map((type) => (
              <option key={type} value={type}>
                {ENTRY_TYPE_ICONS[type]} {ENTRY_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Source filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Source:</label>
          <select
            value={filters.sourceSystem || 'all'}
            onChange={(e) => handleSourceChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="all">All Sources</option>
            {SOURCE_SYSTEMS.map((source) => (
              <option key={source} value={source}>
                {SOURCE_SYSTEM_LABELS[source]}
              </option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-sm text-gray-600">Sort:</span>
          <button
            onClick={() => handleSortChange('recordedAt')}
            className={`text-sm px-2 py-1 rounded ${
              filters.sortBy === 'recordedAt' || !filters.sortBy
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Date {(filters.sortBy === 'recordedAt' || !filters.sortBy) && (filters.sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button
            onClick={() => handleSortChange('title')}
            className={`text-sm px-2 py-1 rounded ${
              filters.sortBy === 'title'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Title {filters.sortBy === 'title' && (filters.sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>

        {/* Toggle more filters */}
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-sm text-indigo-600 hover:text-indigo-700"
        >
          {showMore ? 'Less' : 'More'}
        </button>
      </div>

      {/* More filters */}
      {showMore && (
        <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-3">
          {/* User edited filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">User Edited:</label>
            <select
              value={filters.hasOverrides === undefined ? 'all' : filters.hasOverrides ? 'yes' : 'no'}
              onChange={(e) => handleOverridesChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>
      )}

      {/* Clear filters */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={clearFilters}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}

